import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { RoutePendingFallback } from './components/route-pending-fallback.js';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfirmProvider } from './components/ui/confirm-dialog.js';
import { UndoToastProvider } from './components/ui/undo-toast.js';
import { queryRetryDelayMs, shouldRetryQuery } from './lib/query-retry.js';
import './styles/globals.css';

// Generated by TanStackRouterVite plugin at dev/build time.
import { routeTree } from './routeTree.gen';

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Route-level code splitting (vite.config.ts `autoCodeSplitting`) means every
  // route — including the dashboard and app shell on the daily-use login path,
  // not just rarely-opened admin screens — now fetches its component as a
  // separate chunk. The router's own default (no pending component, ~1s before
  // showing one) leaves a blank area on a slow connection. Shortened both
  // thresholds so the fallback actually appears for a chunk fetch that takes
  // longer than a beat, without flashing on a fast one.
  defaultPendingComponent: RoutePendingFallback,
  defaultPendingMs: 300,
  defaultPendingMinMs: 200,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default is retry: 3, which amplified a 429 into ~4× the requests against
      // the bucket that was already full. See lib/query-retry.ts.
      retry: shouldRetryQuery,
      retryDelay: queryRetryDelayMs,
      // Cross-page floor on refetching. `refetchOnWindowFocus` stays ON — it is
      // genuinely useful for a planning tool several people edit at once — but
      // without a staleTime every alt-tab replayed the whole request burst of the
      // current page. 30s keeps focus-refetch responsive for real edits while
      // making an idle tab-switch free.
      staleTime: 30_000,
    },
    mutations: {
      // A mutation is a user action with side effects; silently repeating it is
      // never the right default (double-charge / double-create class of bug).
      retry: false,
    },
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
        <UndoToastProvider>
          <RouterProvider router={router} />
        </UndoToastProvider>
      </ConfirmProvider>
    </QueryClientProvider>
  </StrictMode>,
);
